import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, Youtube, ExternalLink } from 'lucide-react';
import { companyInfo, navItems, partnerLogos } from '../../data/mock';

const Footer = () => {
  const quickLinks = [
    { label: 'NATS Portal', url: 'https://nats.education.gov.in/' },
    { label: 'NAPS Portal', url: 'https://apprenticeshipindia.gov.in/' },
    { label: 'Skill India Portal', url: 'https://www.skillindia.gov.in/' },
    { label: 'PMKVY', url: 'https://pmkvyofficial.org/' }
  ];

  const socialLinks = [
    { icon: Facebook, url: '#', label: 'Facebook' },
    { icon: Twitter, url: '#', label: 'Twitter' },
    { icon: Linkedin, url: '#', label: 'LinkedIn' },
    { icon: Instagram, url: '#', label: 'Instagram' },
    { icon: Youtube, url: '#', label: 'YouTube' }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Partner Logos Strip */}
      <div className="bg-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-slate-400 text-sm mb-4">Our Affiliations & Partners</p>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="text-center">
              <img src={partnerLogos.nats} alt="NATS" className="h-16 w-auto bg-white rounded-lg p-2" />
              <p className="text-xs text-slate-400 mt-1">Under MoE</p>
            </div>
            <div className="text-center">
              <img src={partnerLogos.naps} alt="NAPS" className="h-16 w-auto bg-white rounded-lg p-2" />
              <p className="text-xs text-slate-400 mt-1">Under MSDE</p>
            </div>
            <img src={partnerLogos.sidh} alt="Skill India Digital Hub" className="h-16 w-auto bg-white rounded-lg p-2" />
            <img src={partnerLogos.tssc} alt="TSSC" className="h-16 w-auto bg-white rounded-lg p-2" />
            <img src={partnerLogos.pmkvy} alt="PMKVY" className="h-16 w-auto bg-white rounded-lg p-2" />
            <img src={partnerLogos.skillIndia} alt="Skill India" className="h-16 w-auto bg-white rounded-lg p-2" />
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img src={companyInfo.logo} alt="Park Skill" className="h-16 w-auto mb-4 bg-white rounded-lg p-2" />
            <p className="text-slate-400 text-sm mb-4">
              Empowering rural youth through skill development, apprenticeship training, and placement services since 2017.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-700 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* External Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Government Portals</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-emerald-400 transition-colors text-sm flex items-center gap-1"
                  >
                    {link.label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 shrink-0" />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 shrink-0" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
                <span className="text-sm">{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Park Skill Development Organization. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/" className="text-slate-500 hover:text-emerald-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/" className="text-slate-500 hover:text-emerald-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
